import {makeStyles} from "@material-ui/styles";
import theme from "../../../theme";
import {getCMSSettings, getCMSSlot} from "tools/cmsBlockSettings";
import React, {useEffect, useState} from "react";
import moment from 'moment';
import 'moment-timezone';
import {Button, Grid, Grow, Slide} from "@material-ui/core";
import {useTranslation} from "tools/i18n";

const useStyles = makeStyles({
	wrapper: {
        display: 'grid',
        margin: 20,
        padding: '0 !important',
        overflow: 'hidden',
        position: 'absolute',
        bottom: '0',
        [theme.breakpoints.down('sm')]: {
            minHeight: 0
        }
	},
    defaultBlock: {
        flexGrow: 1,
        backgroundColor: '#222'
    },
    overlay: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 10,
        justifyContent: 'center',
        alignItems: 'center',
    },
    timer: {
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        '& h2': {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            margin: 0,
            marginBottom: 5,
            fontSize: 50,
            padding: 0,
            color: '#B99354',
            fontFamily: 'Fenice, serif',
            backgroundClip: 'text',
            '-webkit-text-fill-color': 'transparent',
            '-webkit-background-clip': 'text',
            backgroundColor: '#fff',
            transform: 'skew(-15deg)',
            lineHeight: 1,
            '& span': {
                display: 'none',
                fontSize: 12,
                fontFamily: theme.typography.fontFamily
            },
            [theme.breakpoints.down('sm')]: {
                fontSize: 37,
            }
        }
    },
    minutesLeft: {
        marginTop: 0,
        color: '#ffffff',
        fontSize: 18,
        letterSpacing: 0.5,
        textAlign: 'center',
        transition: 'opacity 0.5s ease'
    },
    placeholder: {
        display: 'flex',
        gridColumn: '1/2',
        gridRow: '1/2',
        justifyContent: 'center',
        alignItems: 'center'
    },
    chosenProductPlaceholder: {
        display: 'flex',
        padding: '20px 0',
        '& .image': {
            display: 'flex',
            '& span': {
                flexGrow: 1,
                paddingTop: '96%',
                backgroundColor: '#e0e0e0'
            }
        },
        '& .content': {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            flexGrow: 1,
            '& .text1': {
                width: '60%',
                height: 48,
                marginBottom: 15,
                backgroundColor: '#e0e0e0',
                borderRadius: 2
            },
            '& .text2': {
                width: '100%',
                height: 32,
                marginBottom: 10,
                backgroundColor: '#e0e0e0',
                borderRadius: 2
            },
            '& .addToCart': {
                display: 'flex',
                alignItems: 'center',
                marginTop: 25,
                '& .button': {
                    marginRight: 20,
                    backgroundColor: '#e0e0e0 !important'
                }
            }
        }
    },
    reveal: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        marginTop: 0,
        padding: '14px 34px',
        color: '#000000',
        fontSize: 40,
        fontWeight: 400,
        letterSpacing: 1,
        fontFamily: 'Fenice, serif',
        borderRadius: 0,
        transform: 'translate(-50%, -50%) !important',
        backgroundColor: '#B99354',
        backgroundImage: 'linear-gradient(180deg, #f1f1f1, #a0a0a0)',
        '&:hover': {
            backgroundColor: '#B99354',
            backgroundImage: 'linear-gradient(180deg, #f1f1f1, #a0a0a0)',
        },
        [theme.breakpoints.down('sm')]: {
            padding: '15px',
            maxWidth: 300,
            width: '80%',
            fontSize: 22
        }
    },
    fadeOut: {
        opacity: 0
    }
}, {name: 'Countdown'});

export default function CountdownDecoupled({targetDate}) {
    const {t} = useTranslation();
	const classes = useStyles();

    const countdownDate = new Date(targetDate);
    const [timer, setTimer] = useState({
        hours: 0,
        minutes: 0,
        seconds: 0,
        milliseconds: 0
    });
    const [isPastCountdown, setIsPastCountdown] = useState(false);

    useEffect(() => {
        window.requestAnimationFrame(() => {
            getTimerFromDate();
        });
    }, []);

    useEffect(() => {
        if(!isPastCountdown) {
            let result = checkIsPastCountdown();

            if (result) {
                setIsPastCountdown(true);
            }
        }
    }, [timer]);

    function isCountdownWithinTimeframe() {
        const now = new Date();
        return now < countdownDate;
    }

    function checkIsPastCountdown() {
        const now = new Date();
        return now > countdownDate;
    }

    function leadingZeroes(num, size) {
        num = num.toString();
        while (num.length < size) num = "0" + num;
        return num;
    }

    function getTimerFromDate() {

        const now = new Date();
        const diffMs = countdownDate - now;
        let hours = Math.floor(diffMs / (1000 * 60 * 60)) % 24;
        let minutes = Math.floor(diffMs / (1000 * 60)) % 60;
        let seconds = Math.floor(diffMs / 1000) % 60;
        let hundredth = Math.floor(diffMs / 10) % 100;

        setTimer((timer) => {
            return {
                hours: leadingZeroes(hours, 2),
                minutes: leadingZeroes(minutes, 2),
                seconds: leadingZeroes(seconds, 2),
                hundredth: hundredth
            }
        });

        if (now < countdownDate) {
            window.requestAnimationFrame(()=>{
                getTimerFromDate()
            }
        );

        } else {
            setTimer((timer) => {
                return {
                    hours: leadingZeroes(0, 2),
                    minutes: leadingZeroes(0, 2),
                    seconds: leadingZeroes(0, 2),
                    hundredth: leadingZeroes(0, 2)
                }
            });
        }
    }

	return (
        isCountdownWithinTimeframe() ?
            <div className={[classes.wrapper].join(' ')}>
                <div className={[classes.timer].join(' ')}>
                    <h2>
                        {timer.hours}:
                        <span>{t('countdown-hours')}</span>
                    </h2>
                    <h2>
                        {timer.minutes}:
                        <span>{t('countdown-minutes')}</span>
                    </h2>
                    <h2>
                        {timer.seconds}
                        <span>{t('countdown-seconds')}</span>
                    </h2>
                </div>
                <p className={['slider', classes.minutesLeft, isPastCountdown ? classes.fadeOut : ''].join(' ')} dangerouslySetInnerHTML={{__html: t('countdown-minutes-left')}}></p>
            </div> : ''
	);
}
